import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import useAnalytics from '../../hooks/useAnalytics';

const AnimatedButton = ({ 
  children, 
  to, 
  href, 
  onClick, 
  className = '', 
  type = 'primary', 
  size = 'md',
  isExternal = false,
  disabled = false,
  trackingName = '',
  trackingSection = 'general'
}) => {
  // Analytics hook
  const { trackButtonClick, trackLinkClick } = useAnalytics();

  // Define button styles based on type and size
  // Fonction pour extraire les classes personnalisées liées aux couleurs
  const extractCustomColorClasses = (className) => {
    // Recherche des classes spécifiques de couleur de texte et de fond au survol
    const hasCustomHoverTextColor = className.includes('hover:text-');
    const hasCustomBorderColor = className.includes('border-white') || className.includes('border-gray');
    
    return {
      hasCustomHoverTextColor,
      hasCustomBorderColor
    };
  };
  
  // Handle click with analytics tracking
  const handleClick = (e) => {
    // Track the button click
    const buttonName = trackingName || children?.toString() || 'unnamed-button';
    
    if (to || href) {
      trackLinkClick(buttonName, to || href, isExternal);
    } else {
      trackButtonClick(buttonName, trackingSection);
    }
    
    // Call the original onClick handler if provided
    if (onClick) {
      onClick(e);
    }
  };
  
  const getButtonClasses = () => {
    let baseClasses = 'inline-block font-bold rounded transition-all duration-300 ';
    
    // Extraction des classes personnalisées
    const { hasCustomHoverTextColor, hasCustomBorderColor } = extractCustomColorClasses(className);
    
    // Type styles
    switch(type) {
      case 'primary':
        baseClasses += 'bg-primary text-white hover:bg-blue-700 ';
        break;
      case 'secondary':
        baseClasses += 'bg-accent text-white hover:bg-yellow-600 ';
        break;
      case 'outline':
        if (hasCustomBorderColor && !hasCustomHoverTextColor) {
          // Si c'est un bouton avec bordure blanche/grise, on ajoute une classe pour le texte au survol
          baseClasses += 'bg-transparent border-2 hover:text-primary ';
        } else {
          baseClasses += 'bg-transparent border-2 border-primary text-primary hover:bg-primary hover:text-white ';
        }
        break;
      case 'ghost':
        baseClasses += 'bg-transparent text-primary hover:bg-blue-50 ';
        break;
      default:
        baseClasses += 'bg-primary text-white hover:bg-blue-700 ';
    }
    
    // Size styles
    switch(size) {
      case 'sm':
        baseClasses += 'py-1 px-3 text-sm ';
        break;
      case 'md':
        baseClasses += 'py-2 px-4 text-base ';
        break;
      case 'lg':
        baseClasses += 'py-3 px-6 text-lg ';
        break;
      default:
        baseClasses += 'py-2 px-4 text-base ';
    }
    
    // Disabled state
    if (disabled) {
      baseClasses += 'opacity-50 cursor-not-allowed ';
    }
    
    return `${baseClasses} ${className}`;
  };

  const buttonClasses = getButtonClasses();
  
  // Animation variants
  const buttonVariants = {
    initial: { scale: 1 },
    hover: { scale: 1.05 },
    tap: { scale: 0.95 }
  };

  // Render the appropriate element based on props
  if (to) {
    return (
      <motion.div
        initial="initial"
        whileHover={!disabled ? "hover" : ""}
        whileTap={!disabled ? "tap" : ""}
        variants={buttonVariants}
      >
        <Link to={to} className={buttonClasses} onClick={handleClick}>
          {children}
        </Link>
      </motion.div>
    );
  } else if (href) {
    return (
      <motion.div
        initial="initial"
        whileHover={!disabled ? "hover" : ""}
        whileTap={!disabled ? "tap" : ""}
        variants={buttonVariants}
      >
        <a 
          href={href} 
          className={buttonClasses} 
          onClick={handleClick}
          target={isExternal ? "_blank" : "_self"}
          rel={isExternal ? "noopener noreferrer" : ""}
        >
          {children}
        </a>
      </motion.div>
    );
  } else {
    return (
      <motion.button
        initial="initial"
        whileHover={!disabled ? "hover" : ""}
        whileTap={!disabled ? "tap" : ""}
        variants={buttonVariants}
        className={buttonClasses}
        onClick={handleClick}
        disabled={disabled}
      >
        {children}
      </motion.button>
    );
  }
};

export default AnimatedButton;